import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../routers/api'; // Import the API functions

const SmartEnglishLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine userType from current route
  const userType = location.pathname.includes('/teacher/login') ? 'teacher' : 'student';

  // ✅ Fix: Đọc state từ register để hiển thị thông báo thành công thay vì dùng overlay
  const registeredSuccess = location.state?.registered === true;

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isStudent = userType === 'student';
  const isTeacher = userType === 'teacher';

  // Handle tab switching with URL change
  const handleUserTypeChange = (newUserType) => {
    if (newUserType === 'student') {
      navigate('/student/login');
    } else {
      navigate('/teacher/login');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = 'Please enter your username';
    }

    if (!formData.password) {
      newErrors.password = 'Please enter your password';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const data = await authAPI.login({
        username: formData.username,
        password: formData.password
      });

      // ✅ Redirect based on user type/role
      const userRoles = data.user.roles || [];

      if (userRoles.includes('teacher')) {
        navigate('/teacher/cms');
      }
      else if (userRoles.includes('student')) {
        navigate('/student/dashboard');
      }
      else {
        navigate('/');
      }

    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: error.message || 'Đăng nhập thất bại, vui lòng thử lại.' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <a
              href="/"
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </a>
            <div className="ml-8">
              <div className="text-2xl font-bold text-blue-600">
                SBA<span className="text-gray-800"> ULIS</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* User Type Toggle */}
      <div className="fixed top-4 right-4 z-50 bg-white shadow-lg rounded-lg p-3">
        <div className="flex space-x-2">
          <button
            onClick={() => handleUserTypeChange('student')}
            className={`px-3 py-1 text-xs rounded transition-all duration-200 ${userType === 'student'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            Student
          </button>
          <button
            onClick={() => handleUserTypeChange('teacher')}
            className={`px-3 py-1 text-xs rounded transition-all duration-200 ${userType === 'teacher'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            Teacher
          </button>
        </div>
      </div>

      <div className="flex-1 flex">
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl w-full">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">

              {/* Left Side - Illustration */}
              <div className="hidden lg:flex flex-col items-center justify-center text-center p-8">
                <div className="relative mb-8">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 shadow-2xl">
                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-2xl">🤖</span>
                        </div>
                        <div className="ml-3">
                          <h4 className="font-semibold text-gray-900">AI Teacher</h4>
                          <p className="text-sm text-gray-500">Ready to help you</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg h-40 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto mb-3 flex items-center justify-center">
                            <span className="text-3xl text-white">👩‍🏫</span>
                          </div>
                          <p className="text-sm text-gray-600 font-medium">AI Assistant</p>
                          <p className="text-xs text-gray-500">Waiting for you...</p>
                        </div>
                      </div>
                      <div className="mt-4 flex justify-center">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="max-w-md">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    "Practice with AI – Accelerate your English learning journey."
                  </h2>
                  <p className="text-gray-600">
                    Experience smart learning with 24/7 support from AI Teacher
                  </p>
                </div>
              </div>

              {/* Right Side - Login Form */}
              <div className="w-full max-w-md mx-auto lg:mx-0">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                  {/* Title */}
                  <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {isStudent ? '🎓 Student Login' : '👩‍🏫 Teacher Login'}
                    </h1>
                    <p className="text-gray-600">
                      {isStudent
                        ? 'Access your test preparation system'
                        : 'Manage your tests and students'
                      }
                    </p>
                  </div>

                  {/* ✅ Fix: Hiển thị thông báo đăng ký thành công từ state thay vì overlay */}
                  {registeredSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <p className="text-green-700 text-sm">
                        Đăng ký thành công! Vui lòng đăng nhập để tiếp tục.
                      </p>
                    </div>
                  )}

                  {/* Error Message */}
                  {errors.general && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{errors.general}</p>
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Username Field */}
                    <div>
                      <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                        Username
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="username"
                          name="username"
                          type="text"
                          value={formData.username}
                          onChange={handleInputChange}
                          className={`block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${errors.username
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300 bg-white hover:border-gray-400'
                            }`}
                          placeholder="Enter your username"
                        />
                      </div>
                      {errors.username && (
                        <p className="mt-2 text-sm text-red-600">{errors.username}</p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleInputChange}
                          className={`block w-full pl-10 pr-12 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${errors.password
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300 bg-white hover:border-gray-400'
                            }`}
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                          Logging in...
                        </>
                      ) : (
                        'Login'
                      )}
                    </button>

                    {/* Additional Links */}
                    <div className="space-y-4 text-center">
                      <div>
                        <a
                          href="#"
                          className="text-sm text-blue-600 hover:text-blue-500 hover:underline transition-colors duration-200"
                        >
                          🔑 Forgot password?
                        </a>
                      </div>

                      {isStudent && (
                        <div className="pt-4 border-t border-gray-100">
                          <p className="text-sm text-gray-600">
                            Don't have an account?{' '}
                            <a
                              href="/student/register"
                              className="font-medium text-blue-600 hover:text-blue-500 hover:underline transition-colors duration-200"
                            >
                              Sign up now
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartEnglishLogin;